import type { PoVariant, PoAddress } from '@/lib/po-draft-import-api';

export interface DraftLine {
  key: string;
  sku: string;
  title: string;
  quantity: string;
  pdfPrice: number | null;
  attributes: { key: string; value: string }[];
  variant: PoVariant | null;
  candidates: PoVariant[];
  exactSku: boolean;
  manual: boolean;
  savedVariantId?: string;
  savedStatus?: 'available' | 'unavailable' | 'ambiguous';
}

export const MAX_PDF_BYTES = 10 * 1024 * 1024;
export const ADDRESS_FIELDS: Extract<keyof PoAddress, string>[] = ['firstName', 'lastName', 'company', 'address1', 'address2', 'city', 'province', 'zip', 'countryCode', 'phone'];
export const emptyAddress = (): PoAddress => ({ firstName: '', lastName: '', company: '', address1: '', address2: '', city: '', province: '', zip: '', countryCode: '', phone: '' });

export function normalizePo(raw: string) {
  let value = raw.trim();
  for (let i = 0; i < 4; i++) {
    value = value.replace(/^(?:purchase\s*order|p\.?\s*o\.?)(?:\s*(?:number|no\.?|ref(?:erence)?)\s*)?(?:[\s:#-]+|(?=\d))/i, '')
      .replace(/^#+\s*/, '').trim();
  }
  return value;
}
export function formatOrderDate(raw: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw ?? '');
  if (!m) return raw || '';
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  if (d.getUTCFullYear() !== +m[1]! || d.getUTCMonth() !== +m[2]! - 1 || d.getUTCDate() !== +m[3]!) return raw;
  return `${m[3]}-${m[2]}-${m[1]}`;
}
export function addressIsBlank(a: PoAddress) { return ADDRESS_FIELDS.every((f) => !a[f].trim()); }
export function addressErrors(a: PoAddress): string[] {
  if (addressIsBlank(a)) return [];
  const errs: string[] = [];
  if (!a.address1.trim()) errs.push('Address line 1 is required');
  if (!a.city.trim()) errs.push('City is required');
  if (!a.zip.trim()) errs.push('Postcode is required');
  if (!/^[A-Za-z]{2}$/.test(a.countryCode.trim())) errs.push('Country must be a 2-letter ISO code');
  return errs;
}
export const lineQty = (l: DraftLine) => (/^\d+$/.test(l.quantity.trim()) ? Number(l.quantity) : 0);
export const qtyValid = (l: DraftLine) => { const q = lineQty(l); return q >= 1 && q <= 100000; };
export const money = (n: number, currency: string) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n); } catch { return `${currency} ${n.toFixed(2)}`; }
};
export function priceDiff(l: DraftLine) {
  if (!l.variant || l.pdfPrice == null) return null;
  const cur = Number(l.variant.price);
  return Math.abs(cur - l.pdfPrice) > 0.004 ? { pdf: l.pdfPrice, current: cur } : null;
}
export const lineSubtotal = (lines: DraftLine[]) => lines.reduce((s, l) => s + (l.variant ? lineQty(l) * Number(l.variant.price) : 0), 0);
export const normSku = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
